/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  projects: [],
  recentProjectIds: [],
  activeProject: null,
  activeChapterId: null, // Chapter currently in view / selected (used by right sidebar)
  scenes: {}, // Store scenes by chapterId
  loading: false,
  error: null,
};

export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setProjects: (state, action) => {
      state.projects = action.payload.projects || action.payload;
      state.recentProjectIds = action.payload.recentProjectIds || [];
      state.loading = false;
      state.error = null;
    },
    setActiveProject: (state, action) => {
      state.activeProject = action.payload;
    },
    setActiveChapterId: (state, action) => {
      state.activeChapterId = action.payload;
    },
    addProject: (state, action) => {
      state.projects.unshift(action.payload);
    },
    updateProject: (state, action) => {
      const index = state.projects.findIndex((project) => project._id === action.payload._id);
      if (index !== -1) {
        state.projects[index] = action.payload;
      }
    },
    removeProject: (state, action) => {
      state.projects = state.projects.filter((project) => project._id !== action.payload);
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearActiveProject: (state) => {
      state.activeProject = null;
    },
    addChapterToProject: (state, action) => {
      const { projectId, chapter } = action.payload;
      if (state.activeProject && state.activeProject._id === projectId) {
        if (!Array.isArray(state.activeProject.thinChapters)) {
          state.activeProject.thinChapters = [];
        }
        state.activeProject.thinChapters.push({
          id: chapter._id,
          name: chapter.name,
        });
      }
      // Also update in projects array if it exists there
      const projectIndex = state.projects.findIndex((project) => project._id === projectId);
      if (projectIndex !== -1) {
        if (!Array.isArray(state.projects[projectIndex].thinChapters)) {
          state.projects[projectIndex].thinChapters = [];
        }
        state.projects[projectIndex].thinChapters.push({
          id: chapter._id,
          name: chapter.name,
        });
      }
    },
    addSceneToChapter: (state, action) => {
      const { chapterId, scene } = action.payload;
      // For now, we'll store scenes in a separate state structure
      // This could be enhanced to store scenes within chapters in the future
      if (!state.scenes) {
        state.scenes = {};
      }
      if (!state.scenes[chapterId]) {
        state.scenes[chapterId] = [];
      }
      state.scenes[chapterId].push({
        _id: scene._id,
        id: scene.id,
        name: scene.name,
        description: scene.description,
        type: scene.type,
        position: scene.position,
      });
    },
    updateChapterInProject: (state, action) => {
      const { projectId = null, chapterId, updates = {} } = action.payload;

      if (!chapterId) {
        return;
      }

      const applyUpdates = (chapters) => {
        if (!Array.isArray(chapters)) {
          return;
        }

        const chapterIndex = chapters.findIndex((chapter) => {
          const identifier = chapter.id || chapter._id;
          return identifier === chapterId;
        });

        if (chapterIndex === -1) {
          return;
        }

        chapters[chapterIndex] = {
          ...chapters[chapterIndex],
          ...updates,
        };
      };

      if (state.activeProject && state.activeProject.thinChapters) {
        if (!projectId || state.activeProject._id === projectId) {
          applyUpdates(state.activeProject.thinChapters);
        }
      }

      if (Array.isArray(state.projects) && state.projects.length > 0) {
        const targetProjectIndex = state.projects.findIndex((project) => project._id === projectId);

        if (targetProjectIndex !== -1) {
          applyUpdates(state.projects[targetProjectIndex].thinChapters);
        }
      }
    },
  },
});

export const {
  setProjects,
  setActiveProject,
  setActiveChapterId,
  addProject,
  updateProject,
  removeProject,
  setLoading,
  setError,
  clearError,
  clearActiveProject,
  addChapterToProject,
  addSceneToChapter,
  updateChapterInProject,
} = projectsSlice.actions;

export default projectsSlice.reducer;
